import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, queryMapFactory } from "../common/reducers/utilities";
import { dateInRangeInclusive, maxDate, minDate } from "../common/utils/dateUtils";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import useCredentials from "../common/hooks/credentialsHook";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = hour => hour.id;

export const hoursClaimsSlice = createSlice({
    name: 'hoursClaims',
    initialState: {
        types: [],
        data: {}
    },
    reducers: {
        setTypes: (state, action) => {
            state.types = action.payload;
        },
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(h => state.data[key(h)] = h);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(h => state.data[key(h)] = h);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(h => delete state.data[key(h)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => hoursClaimsSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'hour_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'hour_one');

const fetchHoursClaimTypes = network => network.get('/api/v1/hoursTypes', hoursClaimsSlice.actions.setTypes, visualizeFailure('get'));
const fetchHoursClaimsByID = aggregatingFetch(
    id => network => network.post('/api/v2/fetchHours', [id], data => hoursClaimsSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('api/v2/fetchHours', ids, data => hoursClaimsSlice.actions.set({ ids, data }), visualizeFailure('get'))
)
const fetchHoursClaimsInRange = aggregatingFetch(
    (start, end) => network => network.get(`/api/v2/hours/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, hoursClaimsSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.get(`/api/v2/hours/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}`, hoursClaimsSlice.actions.addOrUpdate, visualizeFailure('get'))
);


export const confirmHoursClaim = (network, id) => network.put(`/api/v1/hours/confirm/${window.encodeURIComponent(id)}`, {}, hoursClaimsSlice.actions.addOrUpdate, visualizeFailure('update'));
export const rejectHoursClaim = (network, id) => network.put(`/api/v1/hours/reject/${window.encodeURIComponent(id)}`, {}, hoursClaimsSlice.actions.addOrUpdate, visualizeFailure('update'));
export const updateHoursClaim  = (network, claim) => network.put('/api/v1/hours', claim, hoursClaimsSlice.actions.addOrUpdate, visualizeFailure('update'));
export const createHoursClaim = (network, claim) => network.post('/api/v1/hours', claim, hoursClaimsSlice.actions.addOrUpdate, visualizeFailure('create'));
export const deleteHoursClaim = (network, claim) =>  network.delete(`/api/v1/hours/${window.encodeURIComponent(claim.id)}`, hoursClaimsSlice.actions.remove, visualizeFailure('remove'));

// #endregion

// #region selectors
const selectTypes = state => {
    return state.hoursClaims.types;
}
const selectAllClaims = state => state.hoursClaims.data;
const selectAllClaimsByMember = createSelector([selectAllClaims], hours => groupBy(Object.values(hours), 'memberid'));
const selectMgrClaimsInRange = (start, end, mgrid) => createSelector([selectAllClaims], hours => Object.values(hours).filter(h => h.memberid !== mgrid && dateInRangeInclusive(h.date, { start, end })) ?? []);
const selectMgrPendingClaimsInRange = (start, end, mgrid) => createSelector([selectAllClaims], hours => Object.values(hours).filter(h => h.confirmed === null && h.memberid !== mgrid && dateInRangeInclusive(h.date, { start, end })) ?? []);
const selectStaffClaimsInRange = (start, end, memberid) => createSelector([selectAllClaimsByMember], hours => hours[memberid]?.filter(h => dateInRangeInclusive(h.date, { start, end })) ?? []);
// #endregion

const listen = {
    hoursCreated: (claim, { dispatch, websocket }) => {
        markFetched(websocket, 'hours_claim', key(claim))
        dispatch(hoursClaimsSlice.actions.addOrUpdate(claim))
    },
    hoursUpdated: (claim, { dispatch }) => dispatch(hoursClaimsSlice.actions.addOrUpdate(claim)),
    hoursRemoved: (claim, { dispatch }) => dispatch(hoursClaimsSlice.actions.remove(claim))
}

const claimsInitByID = initMapFactory(fetchHoursClaimsByID, listen);
registerEntityBasedFetch('hours_claim', claimsInitByID);

const cleanup = { root: hoursClaimsSlice.name, all: state => Object.values(selectAllClaims(state)), removeAll: hoursClaimsSlice.actions.remove, key };

export const canRouteHourClaims = ({credentials, clientConfig}) => credentials.hasRole(['administrator', 'rostering manager', 'recurring rostering manager', 'auditor', 'rostering auditor', 'recurring rostering auditor']) && clientConfig.enableHoursClaims;
export const [useMgrHoursClaimsInRange] = dataHookFactory(selectMgrClaimsInRange, queryMapFactory(fetchHoursClaimsInRange, 'id', claimsInitByID, listen), { cleanup });
export const [useMgrPendingHoursClaimsInRange] = dataHookFactory(selectMgrPendingClaimsInRange, queryMapFactory(fetchHoursClaimsInRange, 'id', claimsInitByID, listen), { cleanup });
export const [useStaffHoursClaimsInRange] = dataHookWithCredentialsFactory(selectStaffClaimsInRange, queryMapFactory(fetchHoursClaimsInRange, 'id', claimsInitByID, listen), {cleanup, condition: credentials => credentials?.hasRole('member') });
export const useSelfClaimsInRange = (start, end) => { 
    const credentials = useCredentials();
    return useStaffHoursClaimsInRange(start, end, credentials.memberID);
}

export const [useHoursClaimTypes,realizeHoursClaimTypes] = dataHookFactory(selectTypes, { run: fetchHoursClaimTypes, immortal: true });

const reducer = { name: hoursClaimsSlice.name, reducer: hoursClaimsSlice.reducer }
export default reducer;